/**
 * The typed API client, generated from openapi-typescript and sealed
 * with openapi-fetch. One client, with middleware that:
 * - Attaches the Firebase bearer token (authFetch pattern)
 * - Preserves traceparent for tracing
 */
import createClient, { type Client } from 'openapi-fetch';
import type { paths } from './types/api';
import { authFetch } from './firebase';
import { API_BASE, RequestRefused } from './client';

/**
 * The one typed client for all API calls. Every request carries
 * the current user's Firebase ID token and a W3C traceparent for
 * distributed tracing.
 *
 * Usage:
 *   const response = await client.GET('/health');
 *   if (response.error) {
 *     if (response.error instanceof RequestRefused) {
 *       // Handle auth errors, not found, etc.
 *     }
 *     return; // Network error or other client error
 *   }
 *   if (response.response?.ok === false) {
 *     throw new RequestRefused(response.response.status, response.data);
 *   }
 *   // response.data is now type-safe
 */
export const client: Client<paths> = createClient<paths>({
  baseUrl: API_BASE,
  fetch: authFetch,
});

/**
 * Response wrapper that converts non-2xx server responses to RequestRefused errors.
 * Pass openapi-fetch response objects through this to get consistent error handling.
 *
 * Usage:
 *   const response = await client.GET('/health');
 *   throwOnServerError(response);
 *   // Throws RequestRefused if response.response.ok is false
 */
export function throwOnServerError<T extends { response?: Response; data?: unknown }>(response: T): void {
  if (response.response && !response.response.ok) {
    const message = typeof response.data === 'string' ? response.data : response.response.statusText;
    throw new RequestRefused(response.response.status, message);
  }
}

/**
 * The data of an answer, after the server's refusal is thrown (`throwOnServerError`) and an answer with no data is
 * refused by name: a read that came back empty says what it was, never `undefined` handed on to a screen.
 *
 * Usage:
 *   const response = await adminClient.GET('/api/review/counts');
 *   return dataOrThrow(response, 'the counts');
 */
export function dataOrThrow<D>(response: { response?: Response; data?: D }, what: string): D {
  throwOnServerError(response);
  if (response.data === undefined) throw new Error(`${what} came back empty`);
  return response.data;
}
